"use client";

import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useVendorProfileEnhanced } from '@/hooks/useVendorProfileEnhanced';
import { useVendorNavCounts } from '@/contexts/VendorNavCountsContext';
import { NotificationDropdown } from '@/components/NotificationDropdown';
import { MessagesDropdown } from '@/components/MessagesDropdown';
import { PendingInvitationsDropdown } from '@/components/settings/PendingInvitationsDropdown';
import { OrganizationSwitcher } from '@/components/settings/OrganizationSwitcher';
import { useIsOrganizationOwner } from '@/hooks/useIsOrganizationOwner';
import { OnboardingTourModal } from '@/components/onboarding/OnboardingTourModal';
import { useOnboardingTour } from '@/hooks/useOnboardingTour';
import { ReadOnlyBanner } from '@/components/ReadOnlyBanner';
import { useReadOnlyMode } from '@/hooks/useReadOnlyMode';
import { AuthenticatedFooter } from '@/components/AuthenticatedFooter';
import {
  LayoutDashboard, 
  Briefcase, 
  FileText, 
  Search, 
  DollarSign, 
  Settings,
  User,
  Users,
  Shield,
  Building,
  LogOut,
  Menu,
  X,
  ChevronDown,
  ChevronRight,
  CalendarDays,
  Route,
  MapPin,
  Layers,
  Folder,
  UserPlus,
  Mail,
  BarChart3,
  MessageSquarePlus,
  MessageSquare,
  Scale,
  History
} from 'lucide-react';
import { GlobalSearch } from '@/components/GlobalSearch';
import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { featureFlags, FEATURE_FLAGS } from '@/lib/featureFlags';

interface LayoutProps {
  children: React.ReactNode;
}

const ClientNavItems = [
  { 
    icon: LayoutDashboard, 
    label: 'Dashboard', 
    href: '/dashboard',
    children: []
  },
  { 
    icon: Briefcase, 
    label: 'Jobs', 
    href: '/jobs',
    children: [
      { label: 'All Jobs', href: '/jobs' },
      { label: 'Create New Job', href: '/jobs/new' }
    ]
  },
  { 
    icon: Folder, 
    label: 'Projects', 
    href: '/projects',
    children: []
  },
  { 
    icon: FileText, 
    label: 'Specifications', 
    href: '/specifications',
    children: []
  },
  { 
    icon: Route, 
    label: 'Milestones', 
    href: '/milestones',
    children: []
  },
  { 
    icon: MapPin, 
    label: 'Addresses', 
    href: '/addresses',
    children: []
  },
  { 
    icon: CalendarDays, 
    label: 'Calendar', 
    href: '/calendar',
    children: []
  },
  { 
    icon: BarChart3, 
    label: 'Reports', 
    href: '/reports',
    children: [
      { label: 'Spend', href: '/reports/spend' },
      { label: 'Vendors', href: '/reports/vendors' },
      { label: 'Cycle Time', href: '/reports/cycle-times' },
      { label: 'Diversity', href: '/reports/diversity' },
    ]
  },
  { 
    icon: UserPlus, 
    label: 'Invite Vendors', 
    href: '/vendors/invite',
    children: []
  },
  { 
    icon: MessageSquarePlus, 
    label: 'Feedback', 
    href: '/feedback',
    children: []
  },
];

const VendorNavItems = [
  { 
    icon: LayoutDashboard, 
    label: 'Dashboard', 
    href: '/dashboard',
    children: []
  },
  { 
    icon: Search, 
    label: 'Browse RFQs', 
    href: '/rfqs',
    children: []
  },
  { 
    icon: DollarSign, 
    label: 'My Bids', 
    href: '/bids',
    children: []
  },
  { 
    icon: Briefcase, 
    label: 'Active Jobs', 
    href: '/jobs',
    children: []
  },
  { 
    icon: CalendarDays, 
    label: 'Calendar', 
    href: '/calendar',
    children: []
  },
  { 
    icon: History, 
    label: 'Bid History', 
    href: '/bid-history',
    children: []
  },
  { 
    icon: MessageSquarePlus, 
    label: 'Feedback', 
    href: '/feedback',
    children: []
  },
];

const AdminNavItems = [
  { 
    icon: LayoutDashboard, 
    label: 'Admin Dashboard', 
    href: '/admin/dashboard',
    children: []
  },
  { 
    icon: BarChart3, 
    label: 'Analytics', 
    href: '/admin/analytics',
    children: []
  },
  { 
    icon: Briefcase, 
    label: 'Jobs', 
    href: '/admin/jobs',
    children: []
  },
  { 
    icon: DollarSign,
    label: 'Payments', 
    href: '/admin/payments',
    children: [
      { label: 'Payment Approvals', href: '/admin/payment-approvals' },
      { label: 'Traditional ACH', href: '/admin/traditional-ach' },
      { label: 'Fix ACH Payment', href: '/admin/fix-payment' },
    ]
  },
  { 
    icon: Building, 
    label: 'Organizations', 
    href: '/admin/organizations',
    children: []
  },
  { 
    icon: Users, 
    label: 'User Management', 
    href: '/admin/users',
    children: []
  },
    { 
      icon: User, 
      label: 'Vendor Management', 
      href: '/admin/vendors',
      children: [
        { label: 'Verification Queue', href: '/admin/verification' },
        { label: 'Capabilities', href: '/admin/capabilities' },
        { label: 'Certifications', href: '/admin/certifications' },
        { label: 'Vendor Invitations', href: '/admin/vendor-invitations' },
      ]
    },
  { 
    icon: Layers, 
    label: 'Spec Management', 
    href: '/admin/specifications',
    children: [
      { label: 'Specifications', href: '/admin/specifications' },
      { label: 'Product Categories', href: '/admin/product-categories' },
      { label: 'Spec Categories', href: '/admin/specification-categories' },
      { label: 'Spec Fields', href: '/admin/specification-fields' },
      { label: 'AI System Prompts', href: '/admin/ai-prompts' },
    ]
  },
  { 
    icon: Route, 
    label: 'Milestone Sets', 
    href: '/admin/milestones',
    children: []
  },
  { 
    icon: FileText, 
    label: 'Dispute Resolution', 
    href: '/admin/disputes',
    children: []
  },
  { 
    icon: Shield, 
    label: 'Security', 
    href: '/admin/security',
    children: [
      { label: 'Security Monitoring', href: '/admin/security-monitoring' },
      { label: 'Flagged Messages', href: '/admin/security' },
    ]
  },
  { 
    icon: Settings, 
    label: 'System Management', 
    href: '/admin/system',
    children: [
      { label: 'Feature Flags', href: '/admin/system' },
      { label: 'Waitlist', href: '/admin/waitlist' },
    ]
  },
  { 
    icon: Scale, 
    label: 'Legal Documents', 
    href: '/admin/legal-documents',
    children: []
  },
  { 
    icon: Mail, 
    label: 'Mass Communications', 
    href: '/admin/mass-communications',
    children: []
  },
  { 
    icon: MessageSquare, 
    label: 'User Feedback', 
    href: '/admin/feedback',
    children: []
  },
  { 
    icon: MessageSquarePlus, 
    label: 'Welcome Messages', 
    href: '/admin/welcome-messages',
    children: []
  },
];

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, organization, logout } = useAuth();
  const { profile: vendorProfile } = useVendorProfileEnhanced();
  const isOwner = useIsOrganizationOwner();
  const { shouldShowTour, completeTour, tourKey } = useOnboardingTour();
  const { isReadOnly } = useReadOnlyMode();
  const { rfqCount, bidsCount, activeJobsCount } = useVendorNavCounts();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [manuallyCollapsed, setManuallyCollapsed] = useState<string[]>([]);
  const [showGlobalSearch, setShowGlobalSearch] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;

  // Check global search feature flag
  useEffect(() => {
    const checkGlobalSearchFlag = async () => {
      const enabled = await featureFlags.isEnabled(FEATURE_FLAGS.GLOBAL_SEARCH);
      setShowGlobalSearch(enabled);
    };
    checkGlobalSearchFlag();
  }, []);

  // Mapping of vendor nav hrefs to their counts
  const vendorNavCounts: Record<string, number> = {
    '/rfqs': rfqCount,
    '/bids': bidsCount,
    '/jobs': activeJobsCount,
  };

  const isAdminRoute = pathname.startsWith('/admin');
  
  // Explicit role-based navigation with no fallback
  let baseNavItems = ClientNavItems;
  if (isAdminRoute) {
    baseNavItems = AdminNavItems;
  } else if (user?.role === 'vendor') {
    baseNavItems = VendorNavItems;
  } else if (user?.role === 'client') {
    baseNavItems = ClientNavItems;
  }
  
  // Add Org Admin nav item for owners only (non-admin routes) when enabled for the organization
  const navItems = !isAdminRoute && isOwner && organization?.org_admin_enabled
    ? [...baseNavItems, { 
        icon: Shield, 
        label: 'Org Admin', 
        href: '/settings/organization',
        children: []
      }]
    : baseNavItems;

  const toggleExpanded = (href: string, isCurrentlyExpanded: boolean) => {
    if (isCurrentlyExpanded) {
      // User is manually collapsing
      setManuallyCollapsed(prev => [...prev, href]);
      setExpandedItems(prev => prev.filter(item => item !== href));
    } else {
      // User is expanding
      setManuallyCollapsed(prev => prev.filter(item => item !== href));
      setExpandedItems(prev => [...prev, href]);
    }
  };

  const isActiveOrChild = (item: any) => {
    if (pathname === item.href) return true;
    if (item.children && item.children.length > 0) {
      return item.children.some((child: any) => pathname === child.href);
    }
    return false;
  };

  const handleLogout = () => {
    logout();
  };

  const getInitials = (email: string) => {
    return email.substring(0, 2).toUpperCase();
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Top Navbar */}
      <header className="bg-background border-b border-border h-16 flex items-center justify-between px-4 sm:px-6 sticky top-0 z-50 backdrop-blur-sm">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="lg:hidden"
          >
            {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
          
          <div className="flex items-center space-x-3">
            <img src="/logo.png" alt="Radius" className="w-8 h-8 rounded-md" />
            <div className="hidden sm:block">
              <h1 className="font-semibold text-foreground text-lg">
                {isAdminRoute ? 'Admin Portal' : 'Radius'}
              </h1>
              {organization && !isAdminRoute && (
                <p className="text-sm text-muted-foreground">
                  {organization.name}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Global Search */}
        {showGlobalSearch && (
          <div className="flex-1 flex justify-center max-w-md mx-4 hidden md:flex">
            <GlobalSearch />
          </div>
        )}

        <div className="flex items-center space-x-2">
              <OrganizationSwitcher />
              <PendingInvitationsDropdown />
              <MessagesDropdown />
              <NotificationDropdown />
          
          <div className="relative group">
            <Button 
              variant="ghost" 
              size="sm" 
              className="flex items-center space-x-3 h-10"
              data-nav-item="user-menu"
            >
              <div className="w-8 h-8 bg-secondary rounded-full flex items-center justify-center">
                <span className="text-secondary-foreground font-semibold text-sm">
                  {user ? getInitials(user.email) : 'U'}
                </span>
              </div>
              <span className="hidden sm:block text-sm font-medium">{user?.email}</span>
            </Button>
            
            {/* User Menu Dropdown */}
            <div className="absolute right-0 mt-1 w-48 bg-background border border-border rounded-md shadow-[var(--shadow-elevated)] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-[var(--transition-fast)] z-50">
              <div className="py-1">
                <div className="px-4 py-3 border-b border-border">
                  <p className="text-sm font-semibold text-foreground">{user?.email}</p>
                  <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
                </div>
                <button
                  onClick={() => navigate('/settings/account')}
                  className="w-full text-left px-4 py-3 text-sm text-foreground hover:bg-secondary transition-colors duration-[var(--transition-fast)] flex items-center space-x-3"
                >
                  <Settings className="h-4 w-4" />
                  <span>Account Settings</span>
                </button>
                <button
                  onClick={() => {
                    if (user?.role === 'vendor') {
                      navigate('/profile/vendor');
                    } else if (user?.role === 'admin') {
                      navigate('/profile/admin');
                    } else {
                      navigate('/profile/client');
                    }
                  }}
                  className="w-full text-left px-4 py-3 text-sm text-foreground hover:bg-secondary transition-colors duration-[var(--transition-fast)] flex items-center space-x-3"
                >
                  <User className="h-4 w-4" />
                  <span>Profile Settings</span>
                </button>
                <button
                  onClick={handleLogout}
                  className="w-full text-left px-4 py-3 text-sm text-destructive hover:bg-secondary transition-colors duration-[var(--transition-fast)] flex items-center space-x-3"
                >
                  <LogOut className="h-4 w-4" />
                  <span>Sign Out</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="flex flex-1">
        {/* Sidebar */}
        <aside className={`
          fixed lg:static inset-y-0 left-0 z-40 w-64 bg-background border-r border-border transform 
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} 
          lg:translate-x-0 transition-transform duration-[var(--transition-smooth)] ease-in-out
          mt-16 lg:mt-0 h-[calc(100vh-4rem)] lg:h-screen
        `}>
          <nav className="p-4 sm:p-6 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = pathname === item.href;
              const isActiveParent = isActiveOrChild(item);
              const hasChildren = item.children && item.children.length > 0;
              const wasManuallyCollapsed = manuallyCollapsed.includes(item.href);
              const isExpanded = (expandedItems.includes(item.href) || (isActiveParent && !wasManuallyCollapsed));
              
              return (
                <div key={item.href}>
                  {/* Parent Item */}
                  <div 
                    data-nav-item={item.href}
                    className={`
                      flex items-center justify-between px-4 py-3 rounded-md text-sm font-medium transition-all duration-[var(--transition-fast)] cursor-pointer
                      ${isActiveParent 
                        ? 'bg-primary text-primary-foreground shadow-[var(--shadow-minimal)]' 
                        : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
                      }
                    `}
                    onClick={() => {
                      // Always navigate to the item's href
                      navigate(item.href);
                      // Also expand/collapse if it has children
                      if (hasChildren) {
                        toggleExpanded(item.href, isExpanded);
                      }
                    }}
                  >
                    <div className="flex items-center space-x-3">
                      <Icon className="h-5 w-5" />
                      <span>{item.label}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {user?.role === 'vendor' && !hasChildren && vendorNavCounts[item.href] > 0 && (
                        <Badge variant="secondary" className="text-xs h-5 min-w-[20px] flex items-center justify-center px-1.5">
                          {vendorNavCounts[item.href]}
                        </Badge>
                      )}
                      {hasChildren && (
                        isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />
                      )}
                    </div>
                  </div>
                  
                  {/* Children Items */}
                  {hasChildren && isExpanded && (
                    <div className="ml-4 mt-1 space-y-1">
                      {item.children.map((child: any) => {
                        const isChildActive = pathname === child.href;
                        return (
                          <Link key={child.href} to={child.href}>
                            <div className={`
                              flex items-center space-x-3 px-4 py-2 rounded-md text-sm transition-all duration-[var(--transition-fast)]
                              ${isChildActive 
                                ? 'bg-primary/10 text-primary font-medium' 
                                : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'
                              }
                            `}>
                              <div className="w-2 h-2 rounded-full bg-current opacity-50" />
                              <span>{child.label}</span>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
            
            {/* Context Switcher */}
            <div className="pt-4 mt-4 border-t border-border">
              {isAdminRoute ? (
                <Link to="/dashboard">
                  <div className="flex items-center space-x-3 px-4 py-3 rounded-md text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-all duration-[var(--transition-fast)]">
                    <span>← Back to Main App</span>
                  </div>
                </Link>
              ) : (
                user?.role === 'admin' && (
                  <Link to="/admin/dashboard">
                    <div className="flex items-center space-x-3 px-4 py-3 rounded-md text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-all duration-[var(--transition-fast)]">
                      <Settings className="h-5 w-5" />
                      <span>Admin Panel</span>
                    </div>
                  </Link>
                )
              )}
            </div>
          </nav>
        </aside>

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-background/80 backdrop-blur-sm lg:hidden z-30"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 lg:ml-0">
          {isReadOnly && <ReadOnlyBanner />}
          <div className="p-4 sm:p-6 lg:p-8">
            {children}
          </div>
        </main>
      </div>

      {/* Authenticated Footer */}
      <AuthenticatedFooter />

      {/* Onboarding Tour Modal */}
      <OnboardingTourModal 
        open={shouldShowTour}
        onOpenChange={(open) => !open && completeTour()}
        onComplete={completeTour}
        tourKey={tourKey}
      />
    </div>
  );
};